import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useState } from "react"
import { FaEdit } from "react-icons/fa"

import { useUpdateAttendance } from "@/hooks/api/useAttendances"
import { usePermissions } from "@/hooks/usePermissions"
import type {
  AttendanceUpdateInput,
  EditAttendanceProps,
} from "@/types/attendance"
import { attendanceUpdateSchema } from "@/types/attendance"
import { Button } from "@chakra-ui/react"

import AttendanceFormDialog from "./AttendanceFormDialog"

const EditAttendance = memo(
  ({ attendance, onSuccess, onCancel }: EditAttendanceProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const updateAttendanceMutation = useUpdateAttendance()
    const { hasPermission } = usePermissions()

    const defaultValues = useMemo<AttendanceUpdateInput>(
      () => ({
        enrollment_id: attendance.enrollment_id ?? "",
        class_session_id: attendance.class_session_id ?? "",
        student_id: attendance.student_id ?? "",
        class_date: attendance.class_date ?? "",
        class_time: attendance.class_time ?? "",
        status: attendance.status ?? null,
        check_in_time: attendance.check_in_time ?? "",
        check_out_time: attendance.check_out_time ?? "",
        is_makeup_class: attendance.is_makeup_class ?? false,
        makeup_for_date: attendance.makeup_for_date ?? "",
        instructor_notes: attendance.instructor_notes ?? "",
        skills_practiced: attendance.skills_practiced
          ? JSON.stringify(attendance.skills_practiced)
          : "",
        behavior_notes: attendance.behavior_notes ?? "",
        parent_notification_sent: attendance.parent_notification_sent ?? false,
        marked_by_user_id: attendance.marked_by_user_id ?? "",
        notes: attendance.notes ?? "",
      }),
      [attendance],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: AttendanceUpdateInput) => {
      await updateAttendanceMutation.mutateAsync({
        attendanceId: attendance.id,
        attendanceData: data,
      })
      onSuccess?.()
    }

    // Don't render if user doesn't have permission
    if (!hasPermission("attendances:update")) {
      return null
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          aria-label={`Edit attendance: ${attendance.id}`}
        >
          <FaEdit fontSize="14px" aria-hidden="true" />
          Edit
        </Button>

        <AttendanceFormDialog
          isOpen={isOpen}
          title="Edit Attendance"
          submitLabel="Update Attendance"
          resolver={zodResolver(attendanceUpdateSchema)}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={updateAttendanceMutation.isPending}
          loadingText="Updating..."
        />
      </>
    )
  },
)

EditAttendance.displayName = "EditAttendance"

export default EditAttendance
export type { EditAttendanceProps }
